<template>
  <div class="progress-page" v-loading="loading">
    <el-form :inline="true" size="small" class="filter-bar" @submit.native.prevent>
      <el-row :gutter="18" class="filter-row">
        <el-col :span="6">
          <el-form-item label="统计年度">
            <el-date-picker
              v-model="filters.year"
              type="year"
              value-format="yyyy"
              placeholder="选择年"
            />
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="统计月份">
            <el-select v-model="filters.month" clearable placeholder="全年">
              <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="计划层级">
            <el-select v-model="filters.planType" placeholder="请选择">
              <el-option label="年计划" value="0" />
              <el-option label="月计划" value="1" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="业务类型">
            <el-select v-model="filters.businessType" clearable placeholder="全部">
              <el-option label="铁运入库" :value="1" />
              <el-option label="汽运入库" :value="3" />
              <el-option label="汽运出库" :value="2" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="客户">
            <el-select
              v-model="filters.custNo"
              clearable
              filterable
              placeholder="全部客户"
            >
              <el-option
                v-for="item in ownerOptions"
                :key="item.id"
                :label="item.deptName"
                :value="item.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="物资">
            <el-select
              v-model="filters.cargoNo"
              clearable
              filterable
              remote
              :remote-method="loadCargoOptions"
              placeholder="请检索"
            >
              <el-option
                v-for="item in cargoOptions"
                :key="item.varNo"
                :label="item.varName"
                :value="item.varNo"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="6">
          <el-form-item label="风险等级">
            <el-select v-model="filters.riskLevel" clearable placeholder="全部">
              <el-option label="低风险" :value="0" />
              <el-option label="中风险" :value="1" />
              <el-option label="高风险" :value="2" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="24" class="filter-actions">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button @click="handleExport">导出</el-button>
        </el-col>
      </el-row>
    </el-form>

    <div class="kpi-grid">
      <div class="kpi-card">
        <span>年/月计划总量</span>
        <strong>{{ fmtTon(overview.planTotalWeight) }}</strong>
        <small>吨</small>
      </div>
      <div class="kpi-card success">
        <span>已执行量</span>
        <strong>{{ fmtTon(overview.planExecuteWeight) }}</strong>
        <small>吨</small>
      </div>
      <div class="kpi-card">
        <span>综合完成率</span>
        <strong>{{ fmtPct(overview.completeRate) }}</strong>
      </div>
      <div class="kpi-card">
        <span>时间进度</span>
        <strong>{{ fmtPct(overview.timeProgress) }}</strong>
      </div>
      <div class="kpi-card" :class="overview.progressDeviation >= 0 ? 'success' : 'danger'">
        <span>进度偏差</span>
        <strong>{{ signedPct(overview.progressDeviation) }}</strong>
      </div>
      <div class="kpi-card danger">
        <span>高风险计划数</span>
        <strong>{{ overview.highRiskPlanCount || 0 }}</strong>
      </div>
    </div>

    <div class="analysis-container">
      <el-tabs v-model="activeTab" class="progress-tabs" @tab-click="resizeCharts">
        <el-tab-pane label="整体维度" name="overall">
          <div class="main-grid">
            <div class="panel">
              <div class="panel-head">
                <span>三业务完成对比</span>
                <div>
                  <el-tag size="mini" type="primary">{{ planTypeLabel(filters.planType) }}</el-tag>
                </div>
              </div>
              <div class="business-list">
                <div v-for="item in businessSummary" :key="item.businessType" class="business-card">
                  <div class="business-top">
                    <div>
                      <b>{{ item.businessTypeLabel }}</b>
                      <p>计划 {{ fmtTon(item.planTotalWeight) }} 吨 / 执行 {{ fmtTon(item.planExecuteWeight) }} 吨</p>
                    </div>
                    <el-tag size="mini" :type="riskTagType(item.riskLevel)">{{ item.riskLevelLabel }}</el-tag>
                  </div>
                  <el-progress
                    :percentage="barPercent(item.completeRate)"
                    :stroke-width="8"
                    :show-text="false"
                    :color="businessColor(item.businessType)"
                  />
                  <div class="business-meta">
                    <span><b>{{ fmtPct(item.completeRate) }}</b>完成率</span>
                    <span><b>{{ fmtPct(item.timeProgress) }}</b>时间进度</span>
                    <span><b :class="item.progressDeviation >= 0 ? 'ok' : 'bad'">{{ signedPct(item.progressDeviation) }}</b>进度偏差</span>
                  </div>
                </div>
                <el-empty v-if="!businessSummary.length" description="暂无业务汇总" :image-size="80" />
              </div>
            </div>
            <div class="panel trend-panel">
              <div class="panel-head">
                <span>年度/月度趋势：计划量 vs 执行量</span>
              </div>
              <div ref="trendChart" class="chart-box trend-chart"></div>
            </div>
          </div>

          <div class="sub-grid">
            <div class="panel">
              <div class="panel-head">
                <span>完成风险矩阵</span>
              </div>
              <div ref="riskChart" class="chart-box chart-sm risk-chart"></div>
            </div>
            <div class="panel">
              <div class="panel-head">
                <span>关注事项分布</span>
              </div>
              <div class="reason-list">
                <div v-for="item in riskReasons" :key="item.code" class="reason-row">
                  <span>{{ item.name }}</span>
                  <el-progress :percentage="reasonPercent(item.count)" :stroke-width="8" :show-text="false" />
                  <b>{{ item.count }}</b>
                </div>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane label="客户维度" name="customer">
          <div class="customer-grid">
            <div class="panel">
              <div class="panel-head">
                <span>客户完成风险排行</span>
              </div>
              <el-table :data="customerRank" class="progress-table" stripe size="small" max-height="430">
                <el-table-column prop="custName" label="客户名称" min-width="170" show-overflow-tooltip />
                <el-table-column prop="businessTypeLabel" label="业务类型" width="95" />
                <el-table-column prop="planTypeLabel" label="计划层级" width="85" />
                <el-table-column prop="planTotalWeight" label="计划量" width="105" align="right" :formatter="fmtTonCol" />
                <el-table-column prop="planExecuteWeight" label="已执行量" width="105" align="right" :formatter="fmtTonCol" />
                <el-table-column label="完成率" width="86">
                  <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
                </el-table-column>
                <el-table-column label="进度偏差" width="92">
                  <template slot-scope="{ row }">
                    <span :class="row.progressDeviation >= 0 ? 'ok' : 'bad'">{{ signedPct(row.progressDeviation) }}</span>
                  </template>
                </el-table-column>
                <el-table-column label="风险" width="84">
                  <template slot-scope="{ row }">
                    <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ row.riskLevelLabel }}</el-tag>
                  </template>
                </el-table-column>
                <el-table-column label="操作" width="70">
                  <template slot-scope="{ row }">
                    <el-button type="text" size="small" @click="openCustomer(row)">查看</el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            <div class="panel">
              <div class="panel-head">
                <span>客户风险结构</span>
              </div>
              <div ref="customerPieChart" class="chart-box chart-sm"></div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <div class="panel detail-panel">
      <div class="panel-head">
        <span>计划明细</span>
      </div>
      <div class="detail-body">
        <el-table :data="detailRows" class="progress-table" stripe size="small" max-height="430">
          <el-table-column prop="planName" label="计划名称" min-width="170" show-overflow-tooltip />
          <el-table-column prop="businessTypeLabel" label="业务类型" width="95" />
          <el-table-column prop="planTypeLabel" label="计划层级" width="85" />
          <el-table-column prop="custName" label="客户" min-width="150" show-overflow-tooltip />
          <el-table-column prop="cargoName" label="物资" min-width="120" show-overflow-tooltip />
          <el-table-column prop="planTotalWeight" label="计划量" width="105" align="right" :formatter="fmtTonCol" />
          <el-table-column prop="planExecuteWeight" label="执行量" width="105" align="right" :formatter="fmtTonCol" />
          <el-table-column label="完成率" width="86">
            <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
          </el-table-column>
          <el-table-column label="时间进度" width="86">
            <template slot-scope="{ row }">{{ fmtPct(row.timeProgress) }}</template>
          </el-table-column>
          <el-table-column label="风险等级" width="86">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="riskTagType(row.riskLevel)">{{ row.riskLevelLabel }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="riskReasonText" label="关注事项" min-width="150" show-overflow-tooltip />
        </el-table>
        <div class="pager-wrap">
          <el-pagination
            background
            layout="total, sizes, prev, pager, next"
            :current-page.sync="filters.page"
            :page-size.sync="filters.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="detailTotal"
            @size-change="loadDetail"
            @current-change="loadDetail"
          />
        </div>
      </div>
    </div>

    <el-drawer
      :visible.sync="drawerVisible"
      size="520px"
      append-to-body
      modal-append-to-body
      wrapper-closable
      destroy-on-close
      :with-header="false"
      custom-class="customer-drawer"
      @closed="disposeDrawerChart"
    >
      <div class="drawer-head">
        <div>
          <h3>{{ customerDetail.custName || '客户详情' }}</h3>
          <p>计划完成趋势与关注事项</p>
        </div>
        <el-button icon="el-icon-close" circle size="small" @click="drawerVisible = false" />
      </div>
      <div class="drawer-body" v-loading="detailLoading">
        <div class="drawer-stats">
          <div><span>计划量</span><b>{{ fmtTon(customerOverview.planTotalWeight) }} 吨</b></div>
          <div><span>已执行量</span><b>{{ fmtTon(customerOverview.planExecuteWeight) }} 吨</b></div>
          <div><span>完成率</span><b>{{ fmtPct(customerOverview.completeRate) }}</b></div>
          <div><span>进度偏差</span><b :class="customerOverview.progressDeviation >= 0 ? 'ok' : 'bad'">{{ signedPct(customerOverview.progressDeviation) }}</b></div>
        </div>
        <h4>客户趋势</h4>
        <div ref="drawerTrendChart" class="chart-box chart-sm"></div>
        <h4>关注事项</h4>
        <div class="drawer-tags">
          <el-tag v-for="item in customerDetail.riskReasons" :key="item" size="small">{{ item }}</el-tag>
        </div>
        <h4>计划明细</h4>
        <el-table :data="customerDetail.planRows || []" class="progress-table" size="mini" max-height="240">
          <el-table-column prop="planName" label="计划名称" min-width="160" show-overflow-tooltip />
          <el-table-column prop="businessTypeLabel" label="业务" width="80" />
          <el-table-column label="完成率" width="80">
            <template slot-scope="{ row }">{{ fmtPct(row.completeRate) }}</template>
          </el-table-column>
        </el-table>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  fetchOverview,
  fetchBusinessSummary,
  fetchMonthTrend,
  fetchRiskReasons,
  fetchCustomerRank,
  fetchDetailPage,
  fetchCustomerDetail,
  fetchOwnerOptions,
  fetchCargoOptions
} from './api'

export default {
  name: 'IntelligentProgress',
  data() {
    return {
      loading: false,
      detailLoading: false,
      activeTab: 'overall',
      filters: {
        year: String(new Date().getFullYear()),
        month: null,
        planType: '0',
        businessType: null,
        custNo: null,
        cargoNo: null,
        riskLevel: null,
        page: 1,
        size: 10
      },
      ownerOptions: [],
      cargoOptions: [],
      overview: {},
      businessSummary: [],
      monthTrend: [],
      riskReasons: [],
      customerRank: [],
      detailRows: [],
      detailTotal: 0,
      customerDetail: {},
      drawerVisible: false,
      charts: {},
      drawerChart: null
    }
  },
  computed: {
    customerOverview() {
      return (this.customerDetail && this.customerDetail.overview) || {}
    }
  },
  mounted() {
    this.loadOwnerOptions()
    this.loadCargoOptions('')
    this.loadData()
    window.addEventListener('resize', this.resizeCharts)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeCharts)
    this.disposeCharts()
    this.disposeDrawerChart()
  },
  methods: {
    query(extra = {}) {
      return {
        ...this.filters,
        year: Number(this.filters.year) || new Date().getFullYear(),
        planType: this.filters.planType || '0',
        ...extra
      }
    },
    async loadData() {
      this.loading = true
      try {
        const query = this.query({ page: undefined, size: undefined })
        const [overview, businessSummary, monthTrend, riskReasons, customerRank] = await Promise.all([
          fetchOverview(query),
          fetchBusinessSummary(query),
          fetchMonthTrend(query),
          fetchRiskReasons(query),
          fetchCustomerRank(query),
        ])
        this.overview = overview || {}
        this.businessSummary = businessSummary || []
        this.monthTrend = monthTrend || []
        this.riskReasons = riskReasons || []
        this.customerRank = customerRank || []
        await this.loadDetail()
        this.$nextTick(this.renderCharts)
      } catch (e) {
        this.$message.error((e && e.message) || '加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadDetail() {
      const pageData = await fetchDetailPage(this.query())
      this.detailRows = (pageData && pageData.records) || []
      this.detailTotal = Number((pageData && pageData.total) || 0)
    },
    async loadOwnerOptions() {
      try {
        const data = await fetchOwnerOptions()
        this.ownerOptions = Array.isArray(data) ? data : []
      } catch (e) {
        this.ownerOptions = []
      }
    },
    async loadCargoOptions(keyword) {
      try {
        const data = await fetchCargoOptions(keyword)
        this.cargoOptions = Array.isArray(data) ? data : []
      } catch (e) {
        this.cargoOptions = []
      }
    },
    handleSearch() {
      this.filters.page = 1
      this.loadData()
    },
    handleReset() {
      this.filters = {
        year: String(new Date().getFullYear()),
        month: null,
        planType: '0',
        businessType: null,
        custNo: null,
        cargoNo: null,
        riskLevel: null,
        page: 1,
        size: 10
      }
      this.loadData()
    },
    async handleExport() {
      try {
        const pageData = await fetchDetailPage(this.query({ page: 1, size: 10000 }))
        const rows = (pageData && pageData.records) || []
        if (!rows.length) {
          this.$message.warning('当前筛选条件下暂无可导出数据')
          return
        }
        const headers = [
          ['planName', '计划名称'],
          ['businessTypeLabel', '业务类型'],
          ['planTypeLabel', '计划层级'],
          ['custName', '客户'],
          ['cargoName', '物资'],
          ['planTotalWeight', '计划量'],
          ['planExecuteWeight', '执行量'],
          ['completeRate', '完成率'],
          ['timeProgress', '时间进度'],
          ['riskLevelLabel', '风险等级'],
          ['riskReasonText', '关注事项'],
          ['intervalStartTime', '计划开始时间'],
          ['intervalEndTime', '计划结束时间']
        ]
        const csv = [
          headers.map(item => this.csvValue(item[1])).join(','),
          ...rows.map(row => headers.map(([key]) => this.csvValue(this.exportValue(row, key))).join(','))
        ].join('\r\n')
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `计划进度分析-${this.query().year}-${this.planTypeLabel(this.filters.planType)}.csv`
        link.click()
        URL.revokeObjectURL(link.href)
      } catch (e) {
        this.$message.error((e && e.message) || '导出失败')
      }
    },
    async openCustomer(row) {
      this.drawerVisible = true
      this.detailLoading = true
      try {
        const data = await fetchCustomerDetail(this.query({
          custNo: row.custNo,
          businessType: row.businessType,
          planType: row.planType,
          page: undefined,
          size: undefined
        }))
        this.customerDetail = data || {}
        this.$nextTick(this.renderDrawerChart)
      } catch (e) {
        this.$message.error((e && e.message) || '客户详情加载失败')
      } finally {
        this.detailLoading = false
      }
    },
    renderCharts() {
      this.disposeCharts()
      this.renderTrendChart()
      this.renderRiskChart()
      this.renderCustomerPieChart()
    },
    renderTrendChart() {
      const el = this.$refs.trendChart
      if (!el) return
      const chart = echarts.init(el)
      const months = (this.monthTrend || []).map(item => item.monthLabel)
      const plans = (this.monthTrend || []).map(item => Number(item.planTotalWeight) || 0)
      const execs = (this.monthTrend || []).map(item => Number(item.planExecuteWeight) || 0)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 8, data: ['计划量', '执行量'] },
        grid: { left: 10, right: 18, top: 42, bottom: 48, containLabel: true },
        xAxis: { type: 'category', data: months, boundaryGap: true },
        yAxis: {
          type: 'value',
          name: '吨',
          nameGap: 18,
          nameTextStyle: {
            align: 'left',
            padding: [0, 0, 0, 2]
          }
        },
        series: [
          { name: '计划量', type: 'bar', data: plans, barWidth: '42%', barMaxWidth: 46, itemStyle: { color: '#94a3b8', borderRadius: [4, 4, 0, 0] } },
          { name: '执行量', type: 'line', smooth: true, data: execs, lineStyle: { width: 3, color: '#1f5fbf' }, itemStyle: { color: '#1f5fbf' } }
        ]
      })
      this.charts.trend = chart
    },
    renderRiskChart() {
      const el = this.$refs.riskChart
      if (!el) return
      const rows = this.detailRows || []
      const chart = echarts.init(el)
      chart.setOption({
        tooltip: {
          formatter: params => {
            const d = params.data || []
            return `${d[3]}<br/>进度偏差：${d[0]}%<br/>剩余量：${this.fmtTon(d[1])} 吨<br/>风险：${d[4]}`
          }
        },
        grid: { left: 12, right: 22, top: 22, bottom: 14, containLabel: true },
        xAxis: { type: 'value', name: '进度偏差%', splitLine: { lineStyle: { type: 'dashed' } } },
        yAxis: { type: 'value', name: '剩余吨', splitLine: { lineStyle: { type: 'dashed' } } },
        series: [{
          type: 'scatter',
          symbolSize: data => Math.max(12, Math.min(34, (Number(data[2]) || 0) / 2600)),
          data: rows.map(row => [
            Number(row.progressDeviation) || 0,
            Number(row.planRemainWeight) || 0,
            Number(row.planTotalWeight) || 0,
            row.custName || row.planName,
            row.riskLevelLabel
          ]),
          itemStyle: {
            color: params => {
              const risk = params.data[4]
              if (risk === '高风险') return '#dc2626'
              if (risk === '中风险') return '#d97706'
              return '#16a34a'
            },
            opacity: 0.86
          }
        }]
      })
      this.charts.risk = chart
    },
    renderCustomerPieChart() {
      const el = this.$refs.customerPieChart
      if (!el) return
      const riskCounts = { low: 0, medium: 0, high: 0 }
      ;(this.customerRank || []).forEach(row => {
        if (row.riskLevel === 2) riskCounts.high += 1
        else if (row.riskLevel === 1) riskCounts.medium += 1
        else riskCounts.low += 1
      })
      const chart = echarts.init(el)
      chart.setOption({
        tooltip: { trigger: 'item' },
        legend: { bottom: 0 },
        series: [{
          type: 'pie',
          radius: ['45%', '68%'],
          center: ['50%', '43%'],
          data: [
            { name: '低风险', value: riskCounts.low, itemStyle: { color: '#16a34a' } },
            { name: '中风险', value: riskCounts.medium, itemStyle: { color: '#d97706' } },
            { name: '高风险', value: riskCounts.high, itemStyle: { color: '#dc2626' } }
          ]
        }]
      })
      this.charts.customerPie = chart
    },
    renderDrawerChart() {
      this.disposeDrawerChart()
      const el = this.$refs.drawerTrendChart
      if (!el) return
      const trend = (this.customerDetail && this.customerDetail.monthTrend) || []
      const chart = echarts.init(el)
      chart.setOption({
        tooltip: { trigger: 'axis' },
        legend: { bottom: 0, data: ['计划量', '执行量'] },
        grid: { left: 48, right: 18, top: 22, bottom: 48 },
        xAxis: { type: 'category', data: trend.map(item => item.monthLabel) },
        yAxis: { type: 'value', name: '吨' },
        series: [
          { name: '计划量', type: 'bar', data: trend.map(item => Number(item.planTotalWeight) || 0), itemStyle: { color: '#94a3b8' } },
          { name: '执行量', type: 'line', smooth: true, data: trend.map(item => Number(item.planExecuteWeight) || 0), itemStyle: { color: '#1f5fbf' } }
        ]
      })
      this.drawerChart = chart
    },
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) this.charts[key].dispose()
      })
      this.charts = {}
    },
    disposeDrawerChart() {
      if (this.drawerChart) {
        this.drawerChart.dispose()
        this.drawerChart = null
      }
    },
    resizeCharts() {
      this.$nextTick(() => {
        Object.keys(this.charts).forEach(key => this.charts[key] && this.charts[key].resize())
        if (this.drawerChart) this.drawerChart.resize()
      })
    },
    fmtTon(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0'
      return n.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    },
    fmtTonCol(row, col, val) {
      return this.fmtTon(val)
    },
    exportValue(row, key) {
      if (['completeRate', 'timeProgress', 'progressDeviation'].includes(key)) {
        return this.fmtPct(row[key])
      }
      return row[key]
    },
    csvValue(value) {
      const text = value === null || value === undefined ? '' : String(value)
      return `"${text.replace(/"/g, '""')}"`
    },
    fmtPct(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0%'
      return n.toFixed(2).replace(/\.00$/, '') + '%'
    },
    signedPct(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return '0%'
      return (n > 0 ? '+' : '') + n.toFixed(2).replace(/\.00$/, '') + '%'
    },
    barPercent(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return 0
      return Math.max(0, Math.min(100, Number(n.toFixed(2))))
    },
    reasonPercent(count) {
      const max = Math.max(1, ...this.riskReasons.map(item => Number(item.count) || 0))
      return Math.round((Number(count) || 0) / max * 100)
    },
    riskTagType(level) {
      if (level === 2) return 'danger'
      if (level === 1) return 'warning'
      return 'success'
    },
    businessColor(type) {
      if (type === 1) return '#1f5fbf'
      if (type === 3) return '#087f5b'
      if (type === 2) return '#c2410c'
      return '#1f5fbf'
    },
    planTypeLabel(type) {
      return type === '1' ? '月计划' : '年计划'
    }
  }
}
</script>

<style scoped lang="scss">
.progress-page {
  background: #edf2fa;
  min-height: 100%;
  color: #1f2937;
  box-sizing: border-box;
}

.filter-bar,
.panel,
.kpi-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  box-shadow: none;
}

.filter-bar {
  padding: 12px 16px 10px;
  margin-bottom: 10px;
  background: #fff;
  box-sizing: border-box;

  .filter-row {
    width: 100%;
    margin: 0 !important;
    padding: 10px 5px 3px;
    background: #f0f7ff;
    box-sizing: border-box;
  }

  ::v-deep .el-col {
    margin-bottom: 7px;
  }

  ::v-deep .el-form-item {
    display: flex;
    margin-bottom: 0;
    margin-right: 0;
    min-width: 0;
  }

  ::v-deep .el-form-item__label {
    flex: 0 0 86px;
    padding-right: 12px;
    box-sizing: border-box;
  }

  ::v-deep .el-form-item__content {
    flex: 1;
    min-width: 0;
  }

  ::v-deep .el-select,
  ::v-deep .el-date-editor {
    width: 100% !important;
  }

  .filter-actions {
    padding-top: 4px;
    text-align: right;
  }
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 10px;
  margin-bottom: 10px;
}

.kpi-card {
  min-height: 92px;
  padding: 13px 14px;
  box-sizing: border-box;

  span {
    color: #606266;
    font-size: 12px;
  }

  strong {
    display: block;
    margin-top: 8px;
    font-size: 22px;
    line-height: 1.1;
    color: #1f2937;
  }

  small {
    display: block;
    margin-top: 8px;
    color: #909399;
  }

  &.success strong {
    color: #087f5b;
  }

  &.danger strong {
    color: #b42318;
  }
}

.analysis-container {
  padding: 0 12px 12px;
  margin-bottom: 10px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  background: #fff;
  box-sizing: border-box;
}

.progress-tabs {
  ::v-deep .el-tabs__header {
    margin: 0 0 10px;
    padding: 0;
    border-bottom: 1px solid #ebeef5;
  }

  ::v-deep .el-tabs__nav-wrap::after {
    display: none;
  }

  ::v-deep .el-tabs__item {
    height: 42px;
    line-height: 42px;
  }
}

.main-grid {
  display: grid;
  grid-template-columns: 0.95fr 1.45fr;
  gap: 10px;
  margin-bottom: 10px;
}

.sub-grid,
.customer-grid {
  display: grid;
  grid-template-columns: 1.25fr 0.75fr;
  gap: 10px;
  margin-bottom: 10px;
}

.panel {
  overflow: hidden;
}

.trend-panel {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 0 14px;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
  box-sizing: border-box;

  small {
    color: #909399;
    font-weight: 400;
  }
}

.business-list {
  padding: 12px 14px 14px;
  display: grid;
  gap: 10px;
}

.business-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px;
  background: #fff;
}

.business-top {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;

  p {
    margin: 5px 0 0;
    color: #606266;
    font-size: 12px;
  }
}

.business-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 11px;
  color: #606266;
  font-size: 12px;

  b {
    display: block;
    color: #1f2937;
    margin-bottom: 3px;
    font-size: 13px;
  }
}

.chart-box {
  width: 100%;
  height: 312px;
  padding: 10px;
  box-sizing: border-box;
}

.trend-chart {
  flex: 1;
  min-height: 360px;
  height: auto;
  padding: 12px 16px 14px;
}

.chart-sm {
  height: 286px;
}

.risk-chart {
  height: 320px;
  padding: 2px 8px 8px;
}

.reason-list {
  padding: 16px 14px;
  display: grid;
  gap: 12px;
}

.reason-row {
  display: grid;
  grid-template-columns: 112px 1fr 36px;
  gap: 10px;
  align-items: center;
  color: #606266;
  font-size: 13px;
}

.detail-panel {
  margin-top: 10px;
}

.detail-body {
  padding: 12px 14px 10px;
  box-sizing: border-box;
}

.pager-wrap {
  padding: 10px 0 0;
  display: flex;
  justify-content: flex-end;
}

.progress-table {
  width: 100%;
}

.progress-table ::v-deep th.el-table__cell {
  padding: 0;
  background: #f5f7fa;
  color: #1f2937;
  font-weight: 600;
  border-right: none;
}

.progress-table ::v-deep td.el-table__cell {
  padding: 8px 0;
  border-right: none;
}

.progress-table ::v-deep .cell {
  line-height: 22px;
}

.progress-table::before,
.progress-table::after {
  display: none;
}

.ok {
  color: #087f5b;
}

.bad {
  color: #b42318;
}

.drawer-head {
  padding: 16px 18px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h3 {
    margin: 0;
    font-size: 16px;
  }

  p {
    margin: 6px 0 0;
    color: #606266;
  }
}

.drawer-body {
  padding: 16px 18px 24px;

  h4 {
    margin: 18px 0 10px;
  }
}

.drawer-stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 9px;

  div {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 10px;
    background: #fff;
  }

  span {
    color: #606266;
    font-size: 12px;
  }

  b {
    display: block;
    margin-top: 5px;
  }
}

.drawer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

@media (max-width: 1180px) {
  .kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .main-grid,
  .sub-grid,
  .customer-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .progress-page {
    padding: 12px;
  }

  .nx-report-header {
    flex-direction: column;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>
